import { useMemo } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../theme/tokens';
import { useAuth } from '../../lib/auth';
import { useNotify } from '../../lib/notify';
import { useSetTalentAction, useTalentActions } from './useTalentActions';
import type { TalentActionItem, TalentActionType } from './talentActionsApi';

/**
 * §4.5 — Acciones del cliente sobre un talent. Toggle 2-state:
 *  - "Quiero conocerlo" → POST type=interested. Cuando ya estaba registrado, no-op
 *    (alreadyRegistered=true).
 *  - "No me interesa"   → POST type=not_interested.
 *
 * Si el user actual ya marcó una acción, ambos botones se muestran pero el "actual"
 * queda highlighted. Cambiar de uno al otro hace flip + (potencialmente) dispara email
 * al recruiter en el BE — el FE NO controla esa lógica, simplemente envía la intent.
 *
 * Debajo: lista de las acciones de los colegas del mismo cliente sobre este talent
 * (incluyendo la del user actual).
 *
 * Modo impersonation (§4.9): los botones se esconden y solo se muestra la lista.
 * El recruiter ve qué acciones tomó el cliente real sin contaminar el estado.
 */
export function TalentActionsPanel({
  shortlistPublicKey,
  talentPublicKey,
}: {
  shortlistPublicKey: string;
  talentPublicKey: string;
}) {
  const { t } = useTranslation();
  const auth = useAuth();
  const notify = useNotify();
  const query = useTalentActions({ shortlistPublicKey, talentPublicKey });
  const setAction = useSetTalentAction({ shortlistPublicKey, talentPublicKey });

  // El email del current user NO está expuesto en /auth/me (solo el sub Guid).
  // Para el "highlight" del toggle del user actual, necesitaríamos email para matchear
  // contra `authorEmail`. Como MVP del FE no expone esto, dejamos el toggle sin highlight
  // y dependemos del feedback del POST (alreadyRegistered=true) + el list refresh para
  // que el user vea su acción en la lista de abajo. Mejora futura: BE expone email en
  // /auth/me y el FE puede highlight el toggle activo del current user.
  const myAction = useMemo<TalentActionItem | undefined>(() => {
    // No tenemos el email del current user en el AuthContext; usamos sub === authorEmail
    // fallback OR la heurística "última acción más reciente". Para versión inicial: si
    // existe alguna acción del user en la lista, asumimos que la primera es la del user
    // (orden DESC por updatedOn/createdOn en el BE). Mejor: el BE podría exponerlo, pero
    // para MVP nos quedamos con esta inferencia leve.
    return undefined;
  }, []);

  const isImpersonating = false /* impersonation not implemented in monorepo */;
  const isMutating = setAction.isPending;

  async function handleClick(type: TalentActionType) {
    try {
      await setAction.mutateAsync({ type });
    } catch {
      notify.error(t('opportunities.talent.actions.error'));
    }
  }

  return (
    <Box
      component="section"
      data-testid="talent-actions-panel"
      sx={{
        padding: '20px 24px',
        background: tokens.color.surface,
        border: `1px solid ${tokens.color.line}`,
        borderRadius: '16px',
      }}
    >
      <Typography
        sx={{
          fontFamily: tokens.font.mono,
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          color: tokens.color.muted,
          mb: '12px',
        }}
      >
        {t('opportunities.talent.actions.sectionTitle')}
      </Typography>

      {!isImpersonating && (
        <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap', mb: '14px' }}>
          <ActionButton
            type="interested"
            label={t('opportunities.talent.actions.interested')}
            highlighted={myAction?.type === 'interested'}
            disabled={isMutating}
            onClick={() => handleClick('interested')}
          />
          <ActionButton
            type="not_interested"
            label={t('opportunities.talent.actions.notInterested')}
            highlighted={myAction?.type === 'not_interested'}
            disabled={isMutating}
            onClick={() => handleClick('not_interested')}
          />
        </Box>
      )}

      {query.isPending && <CircularProgress size={16} />}
      {query.isError && (
        <Alert severity="error" sx={{ mt: '8px' }}>
          {t('opportunities.talent.actions.loadError')}
        </Alert>
      )}
      {query.isSuccess && query.data.items.length === 0 && (
        <Typography sx={{ fontSize: 13, color: tokens.color.muted }}>
          {t('opportunities.talent.actions.empty')}
        </Typography>
      )}
      {query.isSuccess && query.data.items.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="talent-actions-list">
          {query.data.items.map((item) => (
            <ActionRow key={`${item.authorEmail}-${item.createdOn}`} item={item} />
          ))}
        </Box>
      )}
    </Box>
  );
}

function ActionButton({
  type,
  label,
  highlighted,
  disabled,
  onClick,
}: {
  type: TalentActionType;
  label: string;
  highlighted: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={`talent-action-${type}`}
      data-highlighted={highlighted ? 'true' : 'false'}
      sx={{
        padding: '8px 16px',
        borderRadius: '10px',
        border: `1px solid ${highlighted ? tokens.color.accent : tokens.color.line}`,
        background: highlighted ? tokens.color.accentSoft : 'transparent',
        color: highlighted ? tokens.color.accent : tokens.color.ink,
        fontFamily: 'inherit',
        fontSize: 13,
        fontWeight: 600,
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.65 : 1,
        '&:hover': { background: tokens.color.surface2 },
      }}
    >
      {label}
    </Box>
  );
}

function ActionRow({ item }: { item: TalentActionItem }) {
  const { t } = useTranslation();
  const typeLabel =
    item.type === 'interested'
      ? t('opportunities.talent.actions.interested')
      : t('opportunities.talent.actions.notInterested');
  return (
    <Box
      data-testid={`talent-action-row-${item.authorEmail}`}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: '12px',
        fontSize: 13,
        padding: '8px 12px',
        background: tokens.color.surface2,
        borderRadius: '8px',
      }}
    >
      <Typography sx={{ fontSize: 13, color: tokens.color.ink }}>
        {item.authorFullName ?? item.authorEmail}
      </Typography>
      <Typography sx={{ fontSize: 12, color: tokens.color.muted }}>
        {typeLabel}
      </Typography>
    </Box>
  );
}
